import { useContext, useMemo } from 'react'

import { ProductSearchContext } from '@Globals/Algolia/Context'
import { FacetDynamicConfigFacet, FacetDynamicConfigValue, FacetStats } from '@Globals/Algolia/Types'

type EnumerableReturnType = {
  checked: boolean
  count: number
  onChange: () => void
}

type GroupReturnType = {
  showGroupTitle: boolean
}

type RangeReturnType = {
  from?: number
  onChange?: (from?: number, to?: number) => void
  stats: FacetStats[string]
  to?: number
}

export function useEnumerable(facetKey: string, key: string): EnumerableReturnType | null {
  const { facetDynamicConfig, toggleFilter } = useContext(ProductSearchContext)

  const lookupKey = `${facetKey}:${key}`
  const data = facetDynamicConfig[lookupKey] as FacetDynamicConfigValue | undefined
  const checked = data?.checked ?? false
  const count = data?.count ?? 0

  return useMemo(() => ({
    checked,
    count,
    onChange: () => toggleFilter(lookupKey),
  }), [checked, count, lookupKey, toggleFilter])
}

export function useGroup(facetKey: string): GroupReturnType | null {
  const { facetDynamicConfig } = useContext(ProductSearchContext)

  const facetData = facetDynamicConfig[facetKey] as FacetDynamicConfigFacet | undefined
  const showGroupTitle = facetData?.showGroupTitle ?? false

  return useMemo(() => ({
    showGroupTitle,
  }), [showGroupTitle])
}

export function useRange(key: string): RangeReturnType | null {
  const { initialFacetStats, numericFilters, setRangeFilter } = useContext(ProductSearchContext)
  const numericFilter = numericFilters?.[key]
  const initialStats = initialFacetStats?.[key]

  return useMemo(() => {
    if (!initialStats) {
      return null
    }

    return {
      from: numericFilter?.min,
      onChange: (from?: number, to?: number) => setRangeFilter(key, from, to),
      stats: initialStats,
      to: numericFilter?.max,
    }
  }, [key, initialStats, numericFilter, setRangeFilter])
}
